
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Users, MapPin, Upload, Building, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import MainLayout from "@/components/layout/MainLayout";

const formSchema = z.object({
  businessName: z.string().min(2, {
    message: "Business name must be at least 2 characters.",
  }),
  contactName: z.string().min(2, {
    message: "Contact name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  phone: z.string().min(10, {
    message: "Please enter a valid phone number.",
  }),
  eventDate: z.date({
    required_error: "Please select a date for your event.",
  }),
  venueName: z.string().min(2, {
    message: "Venue name must be at least 2 characters.",
  }),
  venueAddress: z.string().min(5, {
    message: "Please provide the venue address.",
  }),
  guestCount: z.string().min(1, {
    message: "Please estimate the number of guests.",
  }),
  eventDetails: z.string().optional(),
  printPreference: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const Business = () => {
  const { toast } = useToast();
  const [fileUploaded, setFileUploaded] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessName: "",
      contactName: "",
      email: "",
      phone: "",
      venueName: "",
      venueAddress: "",
      guestCount: "",
      eventDetails: "",
      printPreference: "",
    },
  });
  
  const onSubmit = (data: FormValues) => {
    console.log(data);
    toast({
      title: "Request submitted!",
      description: "We'll review your business collab request and get back to you soon.",
      duration: 5000,
    });
  };
  
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFileUploaded(true);
      toast({
        title: "File uploaded",
        description: `${e.target.files[0].name} has been uploaded successfully.`,
      });
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 text-center">
            Business{" "}
            <span className="bg-gradient-to-r from-madts-purple to-madts-pink bg-clip-text text-transparent">
              Collaborations
            </span>
          </h1>
          <p className="text-lg text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Partner with MAD-T's for your next business event, pop-up shop, or special promotion!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-6">
              <Card className="bg-gradient-to-br from-madts-purple/10 to-white">
                <CardHeader>
                  <CardTitle>Why Partner With Us?</CardTitle>
                  <CardDescription>
                    Create memorable experiences for your customers and staff
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-madts-purple/20 p-2 rounded-full">
                      <Building className="h-5 w-5 text-madts-purple" />
                    </div>
                    <div>
                      <h3 className="font-medium">Brand Visibility</h3>
                      <p className="text-sm text-gray-600">
                        Increase your brand awareness with custom printed merchandise
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-madts-pink/20 p-2 rounded-full">
                      <Users className="h-5 w-5 text-madts-pink" />
                    </div>
                    <div>
                      <h3 className="font-medium">Customer Engagement</h3>
                      <p className="text-sm text-gray-600">
                        Create interactive experiences your customers will remember
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-madts-blue/20 p-2 rounded-full">
                      <Calendar className="h-5 w-5 text-madts-blue" />
                    </div>
                    <div>
                      <h3 className="font-medium">Event Enhancement</h3>
                      <p className="text-sm text-gray-600">
                        Add a unique, creative element to your business events
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Contact Us Directly</CardTitle>
                  <CardDescription>
                    Have questions? Reach out to our business partnerships team
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-madts-purple" />
                    <span>business@mad-ts.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-madts-purple" />
                    <span>(555) 123-4567</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle>Business Collaboration Request</CardTitle>
              <CardDescription>
                Fill out the form below to request MAD-T's for your business event
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Business Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="businessName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Business Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your business name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="contactName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input placeholder="your.email@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <Input placeholder="(555) 123-4567" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Event Details</h3>
                    
                    <FormField
                      control={form.control}
                      name="eventDate"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Event Date</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant={"outline"}
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <Calendar className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <CalendarComponent
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                                className={cn("p-3 pointer-events-auto")}
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="venueName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Venue Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Event venue name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="guestCount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Expected Guest Count</FormLabel>
                            <FormControl>
                              <Input placeholder="Approximate number of guests" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="venueAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Venue Address</FormLabel>
                          <FormControl>
                            <Input placeholder="Full venue address" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="printPreference"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Print Type Preference</FormLabel>
                          <FormControl>
                            <Input placeholder="T-shirts, totes, posters, etc." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="eventDetails"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Event Details</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe your event and how you'd like MAD-T's to be involved" 
                              {...field}
                              className="resize-none h-24"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Logo Upload</h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <h4 className="text-base font-medium mb-1">
                        {fileUploaded ? "File uploaded" : "Upload your business logo"}
                      </h4>
                      <p className="text-gray-500 mb-4 text-sm">
                        Accepted formats: JPG, PNG, PDF, SVG (Max 10MB)
                      </p>
                      <div className="flex justify-center">
                        <label className="inline-block">
                          <Input 
                            type="file" 
                            accept=".jpg,.jpeg,.png,.pdf,.svg" 
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                          <Button variant="outline" className="cursor-pointer" asChild>
                            <span>
                              <Upload className="mr-2 h-4 w-4" />
                              Browse Files
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-madts-purple to-madts-pink hover:from-madts-purple/90 hover:to-madts-pink/90 text-white"
                    size="lg"
                  >
                    Submit Request
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default Business;
